/*
 * Blushing – client-side favorites (wishlist) stored in localStorage.
 * No account or app required; favorites are per browser/device.
 *
 * Wires up:
 *   [data-favorites-toggle][data-product-handle]  -> add/remove a product
 *   [data-favorites-count]                         -> header count bubble
 *   [data-favorites-list] / [data-favorites-empty] -> the Favorites page
 */
(() => {
  const KEY = 'blushing:favorites:v1';

  const read = () => {
    try {
      const value = JSON.parse(localStorage.getItem(KEY));
      return Array.isArray(value) ? value : [];
    } catch (e) {
      return [];
    }
  };

  const write = (list) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(list));
    } catch (e) {
      /* storage unavailable (e.g. private mode) – fail silently */
    }
    document.dispatchEvent(new CustomEvent('favorites:updated', { detail: { list } }));
  };

  const toggle = (handle) => {
    const list = read();
    const index = list.indexOf(handle);
    if (index === -1) {
      list.push(handle);
    } else {
      list.splice(index, 1);
    }
    write(list);
  };

  const escapeHtml = (value) =>
    String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const sized = (url, width) =>
    url ? url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width : '';

  const money = (cents) => '€' + (Number(cents || 0) / 100).toFixed(2).replace('.', ',');

  const heartSvg = () =>
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none" aria-hidden="true">' +
    '<path d="M10 6.5C10 5 8.5 3.8 6.7 3.8 4.3 3.8 2.8 5.7 2.8 7.9 2.8 10.6 5.4 13 10 16.3 14.6 13 17.2 10.6 17.2 7.9 17.2 5.7 15.7 3.8 13.3 3.8 11.5 3.8 10 5 10 6.5Z" ' +
    'stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  const syncButtons = (root) => {
    const list = read();
    (root || document).querySelectorAll('[data-favorites-toggle]').forEach((btn) => {
      const on = list.indexOf(btn.getAttribute('data-product-handle')) !== -1;
      btn.classList.toggle('is-favorited', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  };

  const syncCount = () => {
    const count = read().length;
    const text = count > 0 ? String(count) : '';
    const hide = count === 0;
    document.querySelectorAll('[data-favorites-count]').forEach((el) => {
      // Only touch the DOM when the value actually changes, so the count write
      // doesn't retrigger the MutationObserver below (which would loop).
      if (el.textContent !== text) el.textContent = text;
      if (el.hidden !== hide) el.hidden = hide;
    });
  };

  const syncAll = () => {
    syncButtons();
    syncCount();
  };

  const cardHTML = (product) => {
    const image = product.featured_image || (product.images && product.images[0]);
    const imageTag = image
      ? '<img src="' + escapeHtml(sized(image, 300)) + '" alt="' + escapeHtml(product.title) + '" loading="lazy">'
      : '';
    return (
      '<li class="favorites-card" data-favorites-card="' + escapeHtml(product.handle) + '">' +
      '<a class="favorites-card__link" href="' + escapeHtml(product.url) + '">' +
      '<span class="favorites-card__media">' + imageTag + '</span>' +
      '<span class="favorites-card__details">' +
      '<span class="favorites-card__title">' + escapeHtml(product.title) + '</span>' +
      '<span class="favorites-card__price">' + money(product.price) + '</span>' +
      '</span>' +
      '</a>' +
      '<button type="button" class="favorites-card__remove" data-favorites-toggle ' +
      'data-product-handle="' + escapeHtml(product.handle) + '" aria-label="Verwijderen uit favorieten">' +
      heartSvg() +
      '</button>' +
      '</li>'
    );
  };

  const renderList = () => {
    const container = document.querySelector('[data-favorites-list]');
    if (!container) return;
    const empty = document.querySelector('[data-favorites-empty]');
    const list = read();
    container.innerHTML = '';
    if (!list.length) {
      if (empty) empty.hidden = false;
      return;
    }
    if (empty) empty.hidden = true;
    list.forEach((handle) => {
      fetch('/products/' + encodeURIComponent(handle) + '.js')
        .then((response) => (response.ok ? response.json() : null))
        .then((product) => {
          if (!product) return;
          container.insertAdjacentHTML('beforeend', cardHTML(product));
          syncButtons(container);
        })
        .catch(() => {});
    });
  };

  const reconcile = () => {
    const container = document.querySelector('[data-favorites-list]');
    if (!container) return;
    const list = read();
    container.querySelectorAll('[data-favorites-card]').forEach((card) => {
      if (list.indexOf(card.getAttribute('data-favorites-card')) === -1) card.remove();
    });
    const empty = document.querySelector('[data-favorites-empty]');
    if (empty) empty.hidden = container.children.length !== 0;
  };

  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-favorites-toggle]');
    if (!btn) return;
    event.preventDefault();
    event.stopPropagation();
    const handle = btn.getAttribute('data-product-handle');
    if (handle) toggle(handle);
  });

  document.addEventListener('favorites:updated', () => {
    syncAll();
    reconcile();
  });

  // The header (and its count bubble) can be re-rendered after load by the
  // theme's section hydration, which resets our JS-applied state. Re-sync BOTH
  // the toggle buttons and the count whenever the DOM changes.
  let scheduled = false;
  const observer = new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      syncAll();
    });
  });

  const init = () => {
    syncAll();
    if (document.body) observer.observe(document.body, { childList: true, subtree: true });
    const drawer = document.querySelector('.favorites-drawer');
    if (drawer) drawer.addEventListener('dialog:open', renderList);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
